/**
 * PATH: packages/ui/src/kanban/card-surface/model/normalizeCardWire.ts
 * FILE_ID: UI-KANBAN-CARDSURFACE-WIRE-001
 * PURPOSE: Normalize lean and full Card wire payloads into one immutable model.
 * WHY: Renderers cannot trust optional, generated, and handwritten shapes to agree.
 * FLOW: unknown wire payload → validate identity → normalize fields/placements → model.
 * DEPENDENCIES: ../types/card.ts.
 * RELATED: ../contract.ts; ../fixtures/goldenCards.ts; ../validation.ts.
 */
import type { BackendSchemas } from '@quotech/api/contracts'

import type { CardStatus } from '../../types/card'

/**
 * The generated backend type currently models `lane_placements` as a string,
 * while the handwritten UI Card expects an array. P0 keeps that discrepancy
 * explicit at this producer boundary until the OpenAPI serializer is repaired.
 */
export type GeneratedCardWire = BackendSchemas['Card']
export type GeneratedLanePlacementsWire = GeneratedCardWire['lane_placements']

export type CardWireShape = 'lean' | 'full'

export interface CardSurfacePlacement {
  id: string
  cardId: string
  laneId: string
  position: number
  laneName: string | null
  boardId: string | null
  boardName: string | null
  boardState: 'active' | 'archived' | 'deleted'
  boardAccessible: boolean
}

export interface CardSurfaceAccess {
  locked: boolean
  state: string | null
  lockId: number | null
}

export interface CardSurfaceModel {
  id: string
  organisationId: number | null
  laneId: string | null
  title: string
  summary: string
  status: CardStatus
  priority: number
  actionDate: string | null
  dueDate: string | null
  startDate: string | null
  primaryEmailId: string | null
  relatedEmailCount: number
  attachmentCount: number
  commentsCount: number
  checklist: Readonly<{ total: number; done: number }>
  access: Readonly<CardSurfaceAccess>
  placements: readonly CardSurfacePlacement[]
  placementId: string | null
  wireShape: CardWireShape
  raw: Readonly<Record<string, unknown>>
}

export interface CardWireIssue {
  field: string
  message: string
}

export type NormalizeCardWireResult =
  | {
      ok: true
      model: CardSurfaceModel
      compatibilityIssues: readonly CardWireIssue[]
    }
  | {
      ok: false
      issues: readonly CardWireIssue[]
    }

const CARD_STATUSES = new Set<CardStatus>([
  'OPEN',
  'SNOOZED',
  'BLOCKED',
  'DONE',
  'ARCHIVED',
])

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function asNullableString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function asBoolean(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback
}

function asBoardState(value: unknown): CardSurfacePlacement['boardState'] {
  return value === 'archived' || value === 'deleted' ? value : 'active'
}

function normalizePlacement(value: unknown): CardSurfacePlacement | null {
  if (!isRecord(value)) return null
  const id = asNullableString(value.id)
  const cardId = asNullableString(value.card)
  const laneId = asNullableString(value.lane)
  if (!id || !cardId || !laneId) return null
  return {
    id,
    cardId,
    laneId,
    position: asNumber(value.position),
    laneName: asNullableString(value.lane_name),
    boardId: asNullableString(value.board),
    boardName: asNullableString(value.board_name),
    boardState: asBoardState(value.board_state),
    boardAccessible: asBoolean(value.board_accessible, true),
  }
}

function normalizePlacements(
  value: unknown,
): { placements: CardSurfacePlacement[]; compatibilityIssues: CardWireIssue[] } {
  let source = value
  const compatibilityIssues: CardWireIssue[] = []

  if (typeof value === 'string') {
    try {
      source = JSON.parse(value) as unknown
      compatibilityIssues.push({
        field: 'lane_placements',
        message: 'Accepted legacy JSON-string placement payload.',
      })
    } catch {
      compatibilityIssues.push({
        field: 'lane_placements',
        message: 'Ignored non-JSON string placement payload.',
      })
      return { placements: [], compatibilityIssues }
    }
  }

  if (source === undefined || source === null) {
    return { placements: [], compatibilityIssues }
  }
  if (!Array.isArray(source)) {
    compatibilityIssues.push({
      field: 'lane_placements',
      message: 'Ignored placement payload that was not an array.',
    })
    return { placements: [], compatibilityIssues }
  }

  const placements: CardSurfacePlacement[] = []
  for (const placement of source) {
    const normalized = normalizePlacement(placement)
    if (normalized) placements.push(normalized)
    else {
      compatibilityIssues.push({
        field: 'lane_placements',
        message: 'Ignored malformed placement entry.',
      })
    }
  }
  return { placements, compatibilityIssues }
}

function normalizedStatus(value: unknown): CardStatus {
  return typeof value === 'string' && CARD_STATUSES.has(value as CardStatus)
    ? (value as CardStatus)
    : 'OPEN'
}

function normalizedChecklist(value: unknown) {
  if (!isRecord(value)) return { total: 0, done: 0 }
  return {
    total: Math.max(0, asNumber(value.total)),
    done: Math.max(0, asNumber(value.done)),
  }
}

export function normalizeCardWire(value: unknown): NormalizeCardWireResult {
  if (!isRecord(value)) {
    return {
      ok: false,
      issues: [{ field: '$', message: 'Card payload must be an object.' }],
    }
  }

  const id = asNullableString(value.id)
  const title = asNullableString(value.title)
  const issues: CardWireIssue[] = []
  if (!id) issues.push({ field: 'id', message: 'Card id must be a non-empty string.' })
  if (!title) issues.push({ field: 'title', message: 'Card title must be a non-empty string.' })
  if (!id || !title) return { ok: false, issues }

  const placementResult = normalizePlacements(value.lane_placements)
  const raw = Object.freeze({ ...value })
  const hasFullOnlyFields =
    Array.isArray(value.related_attachment_ids_json) || Array.isArray(value.audit_log_json)

  return {
    ok: true,
    compatibilityIssues: placementResult.compatibilityIssues,
    model: {
      id,
      organisationId: typeof value.organisation === 'number' ? value.organisation : null,
      laneId: asNullableString(value.lane),
      title,
      summary: typeof value.summary === 'string' ? value.summary : '',
      status: normalizedStatus(value.status),
      priority: asNumber(value.priority),
      actionDate: asNullableString(value.action_date),
      dueDate: asNullableString(value.due_date),
      startDate: asNullableString(value.start_date),
      primaryEmailId: asNullableString(value.primary_email),
      relatedEmailCount: Math.max(
        0,
        asNumber(value.related_email_count, Array.isArray(value.related_emails) ? value.related_emails.length : 0),
      ),
      attachmentCount: Math.max(
        0,
        asNumber(
          value.attachment_count,
          Array.isArray(value.related_attachment_ids_json)
            ? value.related_attachment_ids_json.length
            : 0,
        ),
      ),
      commentsCount: Math.max(0, asNumber(value.comments_count)),
      checklist: normalizedChecklist(value.checklist_summary),
      access: {
        locked: asBoolean(value.access_locked),
        state: asNullableString(value.access_lock_state),
        lockId: typeof value.access_lock_id === 'number' ? value.access_lock_id : null,
      },
      placements: placementResult.placements,
      placementId: asNullableString(value.placement_id),
      wireShape: hasFullOnlyFields ? 'full' : 'lean',
      raw,
    },
  }
}

export function normalizeGeneratedCardWire(
  value: GeneratedCardWire,
): NormalizeCardWireResult {
  return normalizeCardWire(value)
}
